// Reading-order resolution for OCR word boxes (PDF reader).
//
// Boxes may carry an `order` key (reading-order ordinal emitted by the
// enriched backend OCR — Vision's traversal order, RTL-correct). Legacy
// cached pages lack it, so the geometric fallback re-derives order from the
// boxes' positions: rows bucketed by vertical position with a tolerance
// adaptive to the median box height (robust across zoom levels and
// normalized coordinates), rows top→bottom, right-to-left within a row.

function median(nums) {
  if (!nums.length) return 0;
  const s = nums.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Original box indices in reading order. Works on any consistent coordinate
 * space ({nx,ny,nw,nh} or {left,top,width,height}). */
export function orderOcrIndices(boxes) {
  if (!boxes || !boxes.length) return [];
  const idx = boxes.map((_, i) => i);
  if (boxes.every((b) => Number.isFinite(b.order))) {
    return idx.sort((a, b) => boxes[a].order - boxes[b].order);
  }
  const top = (b) => (Number.isFinite(b.ny) ? b.ny : b.top);
  const rightEdge = (b) => (Number.isFinite(b.nx) ? b.nx + b.nw : b.left + b.width);
  const height = (b) => (Number.isFinite(b.nh) ? b.nh : b.height);
  const tol = 0.6 * median(boxes.map(height));

  const rows = []; // { top, items: [boxIdx] }
  idx
    .slice()
    .sort((a, b) => top(boxes[a]) - top(boxes[b]))
    .forEach((i) => {
      const row = rows.find((r) => Math.abs(r.top - top(boxes[i])) < tol);
      if (row) row.items.push(i);
      else rows.push({ top: top(boxes[i]), items: [i] });
    });
  return rows.flatMap((r) =>
    r.items.sort((a, b) => rightEdge(boxes[b]) - rightEdge(boxes[a]))
  );
}

/** Inclusive contiguous slice of `orderedIdx` covering boxes a and b —
 * endpoint order doesn't matter (END may be tapped before START). */
export function rangeBetween(orderedIdx, a, b) {
  const pa = orderedIdx.indexOf(a);
  const pb = orderedIdx.indexOf(b);
  if (pa < 0 || pb < 0) return [];
  const [lo, hi] = pa <= pb ? [pa, pb] : [pb, pa];
  return orderedIdx.slice(lo, hi + 1);
}

/** The selection's text: box texts joined with single spaces, so backend
 * token index k ↔ indices[k] (the morphology endpoints split on whitespace). */
export function textForOrdered(boxes, indices) {
  return indices.map((i) => boxes[i].text).join(" ");
}
